/* eslint-disable max-lines-per-function -- share page with clipboard fallback logic */
"use client";
import { useState } from "react";
import * as React from "react";

import { CopyToClipboardAlert } from "@/components/alerts/alerts";
import { renderShareActionIcon } from "@/utils/share-page-buttons";

interface SharePageProps {
  readonly text: string;
  readonly url: string;
}

function SharePage({ text, url }: SharePageProps): React.JSX.Element {
  const [copyToClipboardAlertIsVisible, setCopyToClipboardAlertIsVisible] = useState(false);

  const shareOptions = [
    {
      name: "Facebook",
      title: "Share on Facebook",
    },
    {
      name: "Twitter",
      title: "Share on Twitter",
    },
    {
      name: "LinkedIn",
      title: "Share on LinkedIn",
    },
    {
      name: "Email",
      title: "Email this page to a friend",
    },
    {
      name: "WhatsApp",
      title: "Share on WhatsApp",
    },
    {
      name: "sms",
      title: "Share via SMS",
    },
  ];

  const getGTMParamValue = ({
    gtmEvent,
    gtmParamName,
  }: {
    gtmEvent: string;
    gtmParamName: string;
  }): string => {
    const events: {
      share: {
        content_type: string;
        item_id: string;
      };
    } = {
      share: {
        content_type: "Webpage",
        item_id: url,
      },
    };

    return events[gtmEvent as keyof typeof events][gtmParamName as keyof typeof events.share];
  };

  const getShareUrl = ({
    platformName,
    urlToShare,
    textString,
  }: {
    platformName: string;
    urlToShare: string;
    textString: string;
  }): string => {
    const shareStringEncode = (str: string): string => encodeURIComponent(str);

    const shareUrl: Record<string, () => string> = {
      default: () => "#",
      email: () => {
        const subject: string = encodeURIComponent(textString);
        const body = encodeURIComponent(
          `${urlToShare}?utm_source=share&utm_campaign=website&utm_medium=email`,
        );
        return `mailto:?subject=${subject}&body=${body}`;
      },
      facebook: () =>
        `https://www.facebook.com/sharer/sharer.php?u=${shareStringEncode(`${urlToShare}`)}&quote=${shareStringEncode(textString)}`,
      linkedin: () =>
        `https://www.linkedin.com/sharing/share-offsite/?url=${shareStringEncode(`${urlToShare}`)}&title=${shareStringEncode(textString)}`,
      sms: () => {
        const message = `${textString} ${urlToShare}?utm_source=share&utm_campaign=website&utm_medium=sms`;
        return `sms:?body=${encodeURIComponent(message)}`;
      },
      twitter: () =>
        `https://twitter.com/intent/tweet?text=${shareStringEncode(textString)}&url=${shareStringEncode(`${urlToShare}&via=Info4PI`)}`,
      whatsapp: () => {
        const message = encodeURIComponent(
          `${textString} ${urlToShare}?utm_source=share&utm_campaign=website&utm_medium=whatsapp`,
        );
        return `whatsapp://send?text=${message}`;
      },
    };

    const shareFunction = shareUrl[platformName.toLowerCase()] || shareUrl.default;
    return shareFunction ? shareFunction() : "";
  };

  return (
    <>
      <CopyToClipboardAlert
        dismiss={() => setCopyToClipboardAlertIsVisible(false)}
        isVisible={copyToClipboardAlertIsVisible}
        message="Link copied to clipboard."
      />
      <ul className="social share-buttons share-page" data-label="Share">
        {shareOptions.map((option) => (
          <li key={option.name}>
            <a
              className="gtm-share share-button"
              data-gtm-param-method={option.name}
              rel="noreferrer"
              target="_blank"
              title={option.title}
              data-gtm-param-content-type={getGTMParamValue({
                gtmEvent: "share",
                gtmParamName: "content_type",
              })}
              data-gtm-param-item-id={getGTMParamValue({
                gtmEvent: "share",
                gtmParamName: "item_id",
              })}
              href={getShareUrl({
                platformName: option.name,
                textString: text,
                urlToShare: url,
              })}
            >
              {renderShareActionIcon({
                iconName: option.name,
              })}
            </a>
          </li>
        ))}
        <li>
          <button
            className="gtm-share share-button"
            data-gtm-param-method="Copy to Clipboard"
            title="Copy Link"
            type="button"
            data-gtm-param-content-type={getGTMParamValue({
              gtmEvent: "share",
              gtmParamName: "content_type",
            })}
            data-gtm-param-item-id={getGTMParamValue({
              gtmEvent: "share",
              gtmParamName: "item_id",
            })}
            onClick={() => {
              const clipBoardAPIisAvailable = !!(
                navigator.clipboard && navigator.clipboard.writeText
              );

              if (clipBoardAPIisAvailable) {
                navigator.clipboard
                  .writeText(url)
                  .then(() => {
                    setCopyToClipboardAlertIsVisible(true);
                    setTimeout(() => {
                      setCopyToClipboardAlertIsVisible(false);
                    }, 5000);
                  })
                  .catch((error) => {
                    // eslint-disable-next-line no-console -- error boundary for clipboard API
                    console.error("Error copying to clipboard:", error);
                  });
              } else {
                try {
                  const textArea = document.createElement("textarea");
                  // eslint-disable-next-line functional/immutable-data -- DOM textarea API
                  textArea.value = url;
                  document.body.appendChild(textArea);
                  textArea.select();
                  document.execCommand("copy");
                  document.body.removeChild(textArea);

                  setCopyToClipboardAlertIsVisible(true);
                  setTimeout(() => {
                    setCopyToClipboardAlertIsVisible(false);
                  }, 5000);
                } catch (error) {
                  // eslint-disable-next-line no-console -- error boundary for clipboard fallback
                  console.error("Error copying to clipboard:", error);
                }
              }
            }}
          >
            {renderShareActionIcon({
              iconName: "copy",
            })}
          </button>
        </li>
      </ul>
    </>
  );
}

export default SharePage;
